/**
 * KAN-746: кнопки перехода заказа по правам из API: "Оформить заказ",
 * "Подтвердить", "Отменить". Каждое действие с подтверждением.
 * KAN-759: actions сужает набор кнопок (модалка CRM: только "Оформить"),
 * disabledActions показывает кнопку недоступной (пустая корзина).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, type ConfirmView } from '@/components/telegram/ConfirmDialog';
import type { OrderAction, OrderPermissions } from '@/types/order';

interface ActionView extends ConfirmView {
  button: string;
  variant: 'default' | 'outline' | 'destructive';
}

const ACTION_VIEWS: Record<OrderAction, ActionView> = {
  place: {
    button: 'Оформить заказ',
    variant: 'default',
    title: 'Оформить заказ?',
    description: 'Корзина станет оформленным заказом, клиент перейдёт на этап продажи.',
    confirm: 'Оформить',
  },
  approve: {
    button: 'Подтвердить',
    variant: 'default',
    title: 'Подтвердить заказ?',
    description: 'Заказ будет отмечен как подтверждённый.',
    confirm: 'Подтвердить',
  },
  cancel: {
    button: 'Отменить',
    variant: 'destructive',
    title: 'Отменить заказ?',
    description: 'Заказ перейдёт в отменённые. Вернуть его в работу из интерфейса нельзя.',
    confirm: 'Отменить заказ',
  },
};

const ACTION_ORDER: readonly OrderAction[] = ['place', 'approve', 'cancel'];

interface OrderActionsProps {
  permissions: OrderPermissions;
  pending: boolean;
  onAction: (action: OrderAction) => void;
  /** Какие переходы показывать (по умолчанию все). */
  actions?: readonly OrderAction[];
  /** Разрешённые, но сейчас недоступные переходы. */
  disabledActions?: readonly OrderAction[];
}

export function OrderActions({
  permissions,
  pending,
  onAction,
  actions = ACTION_ORDER,
  disabledActions = [],
}: OrderActionsProps) {
  const [confirming, setConfirming] = useState<OrderAction | null>(null);
  const allowed = ACTION_ORDER.filter((action) => actions.includes(action) && permissions[action]);
  if (allowed.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="order-actions">
      {allowed.map((action) => (
        <Button
          key={action}
          type="button"
          variant={ACTION_VIEWS[action].variant}
          disabled={pending || disabledActions.includes(action)}
          data-testid={`order-action-${action}`}
          onClick={() => setConfirming(action)}
        >
          {ACTION_VIEWS[action].button}
        </Button>
      ))}
      {confirming && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setConfirming(null)}
          view={ACTION_VIEWS[confirming]}
          testId="order-action-confirm"
          onConfirm={() => {
            onAction(confirming);
            setConfirming(null);
          }}
        />
      )}
    </div>
  );
}
